
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Tag, Calendar, Percent, DollarSign, Users, RotateCcw } from "lucide-react";
import EditCouponDialog from "./EditCouponDialog";
import CouponUsersDialog from "./CouponUsersDialog";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CouponWithServices {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
  discount_value: number;
  credits_per_period?: number;
  max_discount_amount?: number;
  min_order_amount?: number;
  usage_limit?: number;
  used_count: number;
  valid_from: string;
  valid_until?: string;
  is_active: boolean;
  currency?: string;
  services?: Array<{ id: string; name: string; }>;
  coupon_services?: Array<{
    services: { id: string; name: string; };
  }>;
}

interface CouponCardProps {
  coupon: CouponWithServices;
  services: Array<{ id: string; name: string; }>;
  onCouponUpdated: () => void;
  isDeleted?: boolean;
}

const CouponCard = ({ coupon, services, onCouponUpdated, isDeleted = false }: CouponCardProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleEditComplete = () => {
    setEditDialogOpen(false);
    onCouponUpdated();
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('coupons')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', coupon.id);

      if (error) throw error;

      toast({
        title: "Coupon deleted",
        description: "The coupon has been successfully deleted.",
      });
      
      onCouponUpdated();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast({
        title: "Error",
        description: "Failed to delete coupon. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRestore = async () => {
    try {
      const { error } = await supabase
        .from('coupons')
        .update({ 
          deleted_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', coupon.id);

      if (error) throw error;

      toast({
        title: "Coupon restored",
        description: "The coupon has been successfully restored.",
      });
      
      onCouponUpdated();
    } catch (error) {
      console.error('Error restoring coupon:', error);
      toast({
        title: "Error",
        description: "Failed to restore coupon. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getDiscountDisplay = () => {
    const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
    
    if (isCreditBased) {
      const period = coupon.discount_type === 'weekly_credits' ? 'week' : 'month';
      return `${coupon.credits_per_period} free sessions per ${period}`;
    }
    
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% off`;
    } else {
      return `${coupon.discount_value} ${coupon.currency || 'GBP'} off`;
    }
  };

  const getDiscountIcon = () => {
    const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
    
    if (isCreditBased) {
      return <RotateCcw className="h-4 w-4 text-blue-600" />;
    }
    
    return coupon.discount_type === 'percentage' ? 
      <Percent className="h-4 w-4 text-green-600" /> : 
      <DollarSign className="h-4 w-4 text-green-600" />;
  };

  const getDiscountColor = () => {
    const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
    return isCreditBased ? 'text-blue-600' : 'text-green-600';
  };

  const getStatusColor = () => {
    if (!coupon.is_active) return 'bg-gray-100 text-gray-800';
    
    if (coupon.valid_until) {
      const now = new Date();
      const expiryDate = new Date(coupon.valid_until);
      if (now > expiryDate) return 'bg-red-100 text-red-800';
    }
    
    if (coupon.usage_limit && coupon.used_count >= coupon.usage_limit) {
      return 'bg-orange-100 text-orange-800';
    }
    
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = () => {
    if (!coupon.is_active) return 'Inactive';
    
    if (coupon.valid_until) {
      const now = new Date();
      const expiryDate = new Date(coupon.valid_until);
      if (now > expiryDate) return 'Expired';
    }
    
    if (coupon.usage_limit && coupon.used_count >= coupon.usage_limit) {
      return 'Limit Reached';
    }
    
    return 'Active';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';

  return (
    <div className="relative">
      <Card className={cn("h-full", isDeleted && "opacity-60 border-muted")}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  {coupon.name}
                </CardTitle>
                {isDeleted && (
                  <Badge variant="secondary" className="text-xs">
                    Deleted
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="font-mono">
                  {coupon.code}
                </Badge>
                {!isDeleted && (
                  <>
                    <Badge className={getStatusColor()}>
                      {getStatusText()}
                    </Badge>
                    {isCreditBased && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        {coupon.discount_type === 'weekly_credits' ? 'Weekly' : 'Monthly'} Credits
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
            {!isDeleted && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{coupon.name}"? You can restore it later from the deleted coupons list.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete Coupon
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {coupon.description && (
            <p className="text-sm text-gray-600">{coupon.description}</p>
          )}
          
          <div className="flex items-center gap-2">
            {getDiscountIcon()}
            <span className={`text-lg font-bold ${getDiscountColor()}`}>
              {getDiscountDisplay()}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Valid from:</span>
              <p className="font-medium">{formatDate(coupon.valid_from)}</p>
            </div>
            {coupon.valid_until && (
              <div>
                <span className="text-gray-500">Valid until:</span>
                <p className="font-medium">{formatDate(coupon.valid_until)}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">
                {isCreditBased ? 'Claimed by:' : 'Usage:'}
              </span>
              <p className="font-medium">
                {coupon.used_count} / {coupon.usage_limit || '∞'}
              </p>
            </div>
            {!isCreditBased && coupon.min_order_amount && (
              <div>
                <span className="text-gray-500">Min. order:</span>
                <p className="font-medium">{coupon.min_order_amount}</p>
              </div>
            )}
          </div>

          {coupon.services && coupon.services.length > 0 && (
            <div>
              <span className="text-sm text-gray-500">Applicable services:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {coupon.services.map((service) => (
                  <Badge key={service.id} variant="secondary" className="text-xs">
                    {service.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 border-t">
            {isDeleted ? (
              <Button
                onClick={handleRestore}
                className="w-full gap-2"
                variant="default"
              >
                <RotateCcw className="h-4 w-4" />
                Restore Coupon
              </Button>
            ) : (
              <CouponUsersDialog 
                couponId={coupon.id}
                couponCode={coupon.code}
                usedCount={coupon.used_count}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <EditCouponDialog
        coupon={coupon}
        services={services}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onCouponUpdated={handleEditComplete}
      />
    </div>
  );
};

export default CouponCard;
